import { Box, Stack, Text, Flex, Grid, Image } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import ServicesGrid from "../components/custom/ServicesGrid";
import Steps from "../components/custom/Steps";
import ChooseGrid from "../components/custom/ChooseGrid";
import TestimonialSwiper from "../components/custom/TestimonialSwiper";
import TaxGrid from "../components/custom/TaxGrid";
import Stats from "../components/custom/Stats";
import AppointButton from "../components/custom/AppointButton";
import { FilledButton } from "../components/custom/Button";
import RichText from "../components/custom/RichText";
import { useColorModeValue } from "../components/ui/color-mode";
import { Helmet } from "react-helmet-async";
import TextTransition, { presets } from "react-text-transition";

const HomeScreen = () => {
  const textColor = useColorModeValue("gray.600", "gray.500");
  const secondBg = useColorModeValue("#b8fff7", "#090110");
  const [index, setIndex] = useState(0);
  const headingColor = useColorModeValue("#008080", "#66b2b2");

  const popularServices = [
    {
      title: "ITR & GST Filing Services",
      description:
        "Our experts help individuals and businesses with hassle-free ITR and GST filing. We ensure accuracy, maximize deductions, and keep you compliant with all tax regulations while avoiding penalties.",
      buttonText: "File Now",
    },
    {
      title: "Financial Planning",
      description:
        "Smart tax-saving and investment strategies tailored to help you achieve your financial goals.",
      buttonText: "Plan Now",
      url: "expert-assistance",
    },
    {
      title: "Legal Services",
      description: "Reliable legal support for business and personal needs. From agreements to compliance, we simplify complex requirements with ease.",
      buttonText: "Get Legal Help",
      url: "expert-assistance",
    },
    {
      title: "Business Registration",
      description:
        "Register your business seamlessly with our end-to-end support, from choosing the right structure to filing required documents.",
      buttonText: "Register Now",
      url: "business-registration",
    },
    {
      title: "Expert Assistance",
      description:
        "Not sure where to start? Schedule a call with our experienced tax professionals for tailored advice and step-by-step support to simplify your tax journey.",
      buttonText: "Book eCA Today",
      url: "expert-assistance",
    },
  ];


  const steps = [
    { label: "Book a Call & Share Questions", icon: "../assets/expert.png" },
    { label: "Confirm Pricing & Payment", icon: "../assets/pay.png" },
    { label: "Get Expert Consultation", icon: "../assets/consult.png" },
  ];
  const words = [
    "Investments",
    "Tax",
    "Registrations",
    "P&L Planning",
    "Business Laws",
  ];

  useEffect(() => {
    const intervalId = setInterval(
      () => setIndex((i) => i + 1),
      1800
    );
    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      {/* Helmet */}
      <Helmet>
        <title>360fintax - Tax. Uncomplicated.</title>
        <meta
          name="description"
          content="Welcome to 360fintax, your trusted partner for comprehensive tax solutions. We simplify tax processes, ensuring compliance and maximizing your financial efficiency."
        />
        <link rel="canonical" href="https://tax-services-web.vercel.app/" />
      </Helmet>

      {/* Hero Section */}
      <Box
        height={{ base: "800px", md: "600px" }}
        w="full"
        display="flex"
        alignItems={{ base: "center", md: "center", lg: "end" }}
        position="relative"
        justifyContent="center"
        my={{ base: "0", md: "5" }}
        mt={{ base: "14", md: "0" }}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          direction={{ base: "column", md: "row" }}
          gap={{ base: "10", md: "0" }}
        >
          {/* Text1 */}
          <Stack
            px={{ base: "5", md: "10", lg: "20" }}
            gap={5}
            w={{ base: "sm", md: "3xl", lg: "3xl" }}
          >
            <Text
              fontSize={{ base: "3xl", md: "4xl", lg: "6xl" }}
              fontWeight="bold"
              lineHeight="1"
              w={{ base: "xs", md: "fit-content" }}
            >
              360fintax helps you with {" "}
              <Box as="span" display="inline-block">
                <span style={{ color: headingColor }}>
                  <TextTransition springConfig={presets.wobbly}>
                    {words[index % words.length]}
                  </TextTransition>
                </span>
              </Box>
            </Text>
            <Text
              fontSize={{ base: "md", md: "lg", lg: "lg" }}
              color={textColor}
            >
              Say goodbye to paperwork and confusion. Our tax professionals
              ensure a seamless filing process tailored to your needs. Let us
              handle the numbers while you focus on what matters most!
            </Text>
            <Flex>
              <AppointButton text={<FilledButton title="Book a Call Now" />} />
            </Flex>
          </Stack>

          <Image
            src="/assets/hero.png"
            height={{ base: "400px", md: "400px", lg: "500px" }}
            w={{ base: "400px", md: "400px", lg: "500px" }}
            alt="hero"
            loading="eager"
          />
        </Flex>
      </Box>

      {/* Services */}
      <Box bgColor={secondBg} py="10" data-aos="fade-up">
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          px={{ base: "5", md: "10", lg: "20" }}
          mb={{ base: "10", md: "20", lg: "20" }}
          textAlign={{ base: "center", md: "left" }}
          gap={{ base: "3", md: "0" }}
        >
          <Text
            fontSize={{ base: "xl", md: "3xl", lg: "5xl" }}
            fontWeight="bold"
            lineHeight="1"
          >
            Simplifying Taxes, Empowering You
          </Text>
          <Text
            fontSize={{ base: "xs", md: "lg", lg: "xl" }}
            textAlign={{ base: "center", md: "justify" }}
            w={{ base: "4xs", md: "md", lg: "2xl" }}
            color={textColor}
          >
            At 360fintax, we take the hassle out of tax filing and business
            compliance. Our mission is to simplify your tax journey with expert
            support, making your financial management stress-free and efficient.
          </Text>
        </Grid>
        <ServicesGrid />
      </Box>

      {/* Popular Services */}
      <Box py="16" data-aos="fade-up">
        <RichText title="Popular Services" colorText="Services" />
        <TaxGrid services={popularServices} />
      </Box>

      {/* Steps */}
      <Steps
        steps={steps}
        buttonText="Get Tax Advice"
        title="3 Easy Steps to Stress-Free Tax Advice"
        colorText="Stress-Free"
      />

      {/* Why Choose Us */}
      <Stack my={10} px={{ base: "5", md: "0" }} gap={10} data-aos="fade-up">
        {" "}
        <RichText
          title="What sets us apart?"
          colorText="apart?"
          description="We Handle the Details, So You Can Focus on What Matters Most."
        />
        <ChooseGrid showImage={true} />
      </Stack>

      {/* Stats */}
      <Stats />

      {/* Testmonials */}
      <Stack
        textAlign="center"
        gap={{ base: "20", md: "10" }}
        py={{ base: "10", md: "10", lg: "20" }}
        alignItems="center"
        data-aos="fade-up"
      >
        <RichText
          title="Trusted by Clients,Proven Results"
          colorText="Proven Results"
          description="Discover how we've helped individuals and businesses stay compliant and stress-free, year after year."
          w={{ base: "xs", md: "3xl" }}
        />

        <TestimonialSwiper />
      </Stack>
    </>
  );
};

export default HomeScreen;
